import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Actions, ofActionSuccessful, Select, Store } from '@ngxs/store';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  Observable,
  Subject,
  tap
} from 'rxjs';

import { ReactiveComponent } from 'src/app/components/shared/reactive-component';
import {
  BaseData,
  ChargeAccountDepartment,
  Department,
  NormalizedTicket,
  TicketStatus
} from 'src/app/models';
import {
  GetBusinessLines,
  GetDepartments,
  ResetDepartments
} from 'src/app/store/payment/payment.action';
import { PaymentState } from 'src/app/store/payment/payment.state';
import {
  AddTaxExemption,
  RemoveTaxExemption
} from 'src/app/store/tickets/tickets.action';
import { TicketsStateSelectors } from 'src/app/store/tickets/tickets.selectors';

export interface DepartmentChargeAccountPaymentModalData {
  department: Department;
  charge_account: ChargeAccountDepartment;
}

export interface IDepartmentChargeAccountPaymentModal {}

@Component({
  selector: 'pos-department-charge-account-payment',
  templateUrl: './department-charge-account.modal.html',
  styleUrls: ['./department-charge-account.modal.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DepartmentChargeAccountPaymentModal
  extends ReactiveComponent
  implements OnDestroy
{
  @ViewSelectSnapshot(TicketsStateSelectors.normalizedTicket)
  readonly ticket: NormalizedTicket;

  @Select(PaymentState.departments)
  readonly departments$: Observable<Department[]>;
  // @ViewSelectSnapshot(TicketsStateSelectors.normalizedTicket)
  // readonly selectedTicket: NormalizedTicket;

  businessLines: BaseData[];
  minLengthTerm = 3;
  departmentLoading = false;
  selectedDepartment: Department;
  readonly searchKey$ = new Subject<string>();

  private selectedBusinessLine: BaseData;

  constructor(
    private store: Store,
    private actions$: Actions,
    private modalController: ModalController
  ) {
    super();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.store.dispatch(new ResetDepartments());
  }

  *onSubscribe() {
    this.store.dispatch(new GetBusinessLines());

    yield this.searchKey$
      .pipe(
        filter((res) => res && res.length >= this.minLengthTerm),
        distinctUntilChanged(),
        debounceTime(500),
        tap(() => (this.departmentLoading = true))
      )
      .subscribe((typeahead) => {
        this.store.dispatch(
          new GetDepartments(typeahead, this.selectedBusinessLine?.id)
        );
      });

    yield this.actions$
      .pipe(ofActionSuccessful(GetDepartments))
      .subscribe(() => (this.departmentLoading = false));
  }

  onSelectBusinessLine(event) {
    this.selectedBusinessLine = event.detail.value;
    this.store.dispatch(new ResetDepartments());
    this.selectedDepartment = undefined;
  }

  onCloseModal(charge_account?: ChargeAccountDepartment) {
    if (!charge_account) {
      this.modalController.dismiss();
    } else {
      this.modalController.dismiss({
        department: this.selectedDepartment,
        charge_account
      });
    }
  }

  onSelectChargeAccount(charge_account: ChargeAccountDepartment) {
    //Only attempt Add/Remove Tax exemption if the ticket does not have a payment on it.

    if (
      [TicketStatus.PARTIAL_PAID, TicketStatus.PAID].every(
        (x) => x !== this.ticket.status
      )
    ) {
      //If department is tax exempted, add tax exemption to the ticket.
      if (this.selectedDepartment?.tax_exempt) {
        this.store.dispatch(
          new AddTaxExemption(this.ticket.device_ticket_uuid)
        );
      } else {
        this.store.dispatch(new RemoveTaxExemption(this.ticket));
      }
    }

    this.onCloseModal(charge_account);
  }
}
